import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            {t("categories.title")}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            {t("categories.subtitle")}
          </p>
        </div>

        <div className="flex justify-center mb-8" ref={dropdownRef}>
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3 bg-background-100 border border-background-200/70 rounded-xl text-sm font-medium text-foreground-700 hover:border-primary-300 transition-all duration-200 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                {t("nav.allCategories")}
              </span>
              <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-full bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden z-40 animate-[fadeIn_0.15s_ease-out]">
                <div className="max-h-72 overflow-y-auto py-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors duration-150"
                    >
                      <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col items-center gap-2 p-4 md:p-5 bg-background-100 hover:bg-primary-100/60 border border-background-200/60 hover:border-primary-300/60 rounded-xl transition-all duration-200 cursor-pointer"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-200">
                <i className={`${cat.icon} text-lg`}></i>
              </div>
              <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-foreground-900 text-center whitespace-nowrap">
                {cat.name}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}